// Premier lancement, en trois écrans : ce que fait la carte et ce qu'elle ne fait pas, les règles, la localisation.
import { useEffect, useRef, useState } from 'react';
import { RulesList, WhatItIsNot } from './menu/Rules';
import './onboarding.css';

export const ONBOARDING_KEY = 'pousse:premier-lancement';

export function onboardingDone(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === 'vu';
  } catch {
    return true;
  }
}

function Hex() {
  return (
    <svg viewBox="0 0 40 46" width="56" height="64" aria-hidden="true" className="accueil-hex">
      <path d="M20 1 L20 45 L1 34 L1 12 Z" style={{ fill: 'var(--cepe-60)' }} />
      <path d="M20 1 L39 12 L39 34 L20 45 Z" style={{ fill: 'var(--girolle-60)' }} />
    </svg>
  );
}

export function Onboarding({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState(0);
  const [asking, setAsking] = useState(false);
  const title = useRef<HTMLHeadingElement>(null);

  // À chaque écran, le focus va au titre : le lecteur d'écran annonce la nouvelle page.
  useEffect(() => {
    if (document.documentElement.classList.contains('premier')) title.current?.focus();
  }, [step]);

  const finish = async (consent: boolean) => {
    if (consent) {
      setAsking(true);
      // Dexie n'est chargé qu'ici : l'écran d'accueil fait partie de la coque prérendue, qui doit rester légère.
      const { CONSENT_KEY, setSetting } = await import('../db');
      await setSetting(CONSENT_KEY, { accepted_at: new Date().toISOString(), version: 1 });
      // La question du navigateur vient après l'explication, jamais avant.
      await new Promise<void>((resolve) =>
        navigator.geolocation?.getCurrentPosition(() => resolve(), () => resolve(), { timeout: 10000, maximumAge: 600000 }) ?? resolve(),
      );
    }
    try {
      localStorage.setItem(ONBOARDING_KEY, 'vu');
    } catch {
      // Stockage indisponible : l'accueil reviendra au prochain lancement.
    }
    document.documentElement.classList.remove('premier');
    onDone();
  };

  const steps = [
    <>
      <Hex />
      <h2 ref={title} tabIndex={-1} className="accueil-titre">
        Où aller ce week-end, et pourquoi là
      </h2>
      <p className="accueil-texte">
        Chaque matin, la carte estime la pousse du cèpe et de la girolle, maille par maille, à partir de la pluie des dernières
        semaines, de la température, du relief et de la forêt. Touchez une maille : elle dit pourquoi.
      </p>
      <WhatItIsNot />
    </>,
    <>
      <h2 ref={title} tabIndex={-1} className="accueil-titre">
        Les règles du terrain
      </h2>
      <RulesList />
    </>,
    <>
      <h2 ref={title} tabIndex={-1} className="accueil-titre">
        Votre position, seulement si vous le voulez
      </h2>
      <ul className="accueil-points">
        <li>
          <strong>Quand :</strong> pendant une sortie que vous démarrez, ou quand vous touchez « Me localiser ». Jamais en arrière-plan.
        </li>
        <li>
          <strong>Ce qui est enregistré :</strong> la trace de la sortie et vos trouvailles, sur ce téléphone d'abord, puis envoyées au
          projet. La position exacte des trouvailles n'est montrée à personne.
        </li>
        <li>
          <strong>Combien de temps :</strong> deux ans au plus.
        </li>
        <li>
          <strong>Vos droits :</strong> export et suppression à tout moment, depuis Menu, puis Mes sorties.
        </li>
      </ul>
      <p className="accueil-texte">La carte marche aussi sans votre position.</p>
    </>,
  ];

  return (
    <div className="accueil-ecran" role="dialog" aria-modal="true" aria-label="Premier lancement">
      <div className="accueil-contenu">
        <p className="accueil-etape" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span key={i} className={i === step ? 'accueil-point accueil-point-actif' : 'accueil-point'} />
          ))}
        </p>
        <p className="visually-hidden">Étape {step + 1} sur 3</p>
        {steps[step]}
      </div>
      <div className="accueil-actions">
        {step < 2 ? (
          <>
            <button type="button" className="bouton bouton-principal bouton-large bouton-haut" onClick={() => setStep(step + 1)}>
              {step === 0 ? 'Compris' : 'Je respecterai ces règles'}
            </button>
            {step > 0 && (
              <button type="button" className="bouton bouton-secondaire bouton-large" onClick={() => setStep(step - 1)}>
                Retour
              </button>
            )}
          </>
        ) : (
          <>
            <button type="button" className="bouton bouton-principal bouton-large bouton-haut" onClick={() => void finish(true)} disabled={asking}>
              {asking ? 'En attente du téléphone…' : 'Autoriser la localisation'}
            </button>
            <button type="button" className="bouton bouton-secondaire bouton-large" onClick={() => void finish(false)} disabled={asking}>
              Plus tard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
